import { ModalAction } from '@overlayqa/shared/ui';
import { useState, useCallback, useId } from 'react';
import { clsx } from 'clsx';
import { HugeiconsIcon } from '@hugeicons/react';
import { Image01Icon, Folder01Icon, BulbIcon } from '@hugeicons/core-free-icons';
import { figmaLinkTypeForNodeId, normalizeFigmaNodeIdParam } from '@overlayqa/shared';
import type { ParsedFigmaUrl } from '@overlayqa/shared';
import { toUserFacingMessage } from '@overlayqa/shared';

interface FigmaUrlInputProps {
  value?: string;
  onChange?: (url: string, parsed: ParsedFigmaUrl | null) => void;
  onSave: (parsed: ParsedFigmaUrl) => Promise<void>;
  disabled?: boolean;
  className?: string;
  context?: 'project' | 'issue';
  /** Hide the internal save button when the host form submits instead (e.g. the create-project modal, where onSave is a no-op). */
  showSaveButton?: boolean;
  /** Hide the internal "Figma URL" label when the host already labels the field (e.g. the create/edit-project modals, which render their own "Figma File (optional)" label wired to this input). Avoids a redundant second label. */
  showLabel?: boolean;
  /**
   * Input tier, mirroring SelectDropdown's `size`. `lg` is the comfortable
   * 40px `.input-lg` tier every other field inside a modal sits on
   * (styles/index.css, d77fc3d1); the default keeps the 32px dense tier for
   * the settings page, where this field renders alone. Before 2026-09-03 the
   * two project modals had one 32px field between 40px siblings.
   */
  size?: 'default' | 'lg';
  /**
   * The internal save button's weight. `secondary` where the host already has
   * a primary on the same screen, e.g. the Project Settings modal, whose Save is
   * its one primary (Carbon: "Each page should have only one primary button").
   */
  saveButtonVariant?: 'primary' | 'secondary';
  /** Rendered in the same row as the save button, e.g. a Cancel that folds the field away. */
  saveButtonAccessory?: React.ReactNode;
}

// Client-side URL parser (mirrors server logic)
function parseFigmaUrl(url: string): ParsedFigmaUrl {
  const patterns = [
    // node-id charset: digits/colon/dash, %-escapes (our own pin-save emits
    // node-id=150%3A29), letter prefixes (I… instance ids) and ';' paths.
    { regex: /figma\.com\/file\/([a-zA-Z0-9]+)(?:\/([^?]+))?(?:\?.*node-id=([0-9A-Za-z%:;-]+))?/, type: 'file' as const },
    { regex: /figma\.com\/proto\/([a-zA-Z0-9]+)(?:\/([^?]+))?(?:\?.*node-id=([0-9A-Za-z%:;-]+))?/, type: 'proto' as const },
    { regex: /figma\.com\/design\/([a-zA-Z0-9]+)(?:\/([^?]+))?(?:\?.*node-id=([0-9A-Za-z%:;-]+))?/, type: 'design' as const },
  ];

  for (const { regex, type } of patterns) {
    const match = url.match(regex);
    if (match) {
      const fileKey = match[1];
      const fileName = match[2] ? decodeURIComponent(match[2].replace(/-/g, ' ')) : undefined;
      const nodeId = normalizeFigmaNodeIdParam(match[3]);
      const embedUrl = `https://www.figma.com/embed?embed_host=overlayqa&url=${encodeURIComponent(url)}`;

      // Canvas node-ids (0:0, 0:1) come from copied FILE links, not frames.
      const linkType = figmaLinkTypeForNodeId(nodeId);
      const displayType = linkType === 'frame' ? 'Figma Frame' : 'Figma File';
      const icon = linkType === 'frame' ? 'frame' : 'file';

      return { fileKey, fileName, nodeId, type, isValid: true, embedUrl, linkType, displayType, icon };
    }
  }

  return {
    fileKey: '',
    type: 'file',
    isValid: false,
    error: 'Invalid Figma URL. Paste a link like figma.com/design/...',
    linkType: 'file',
    displayType: 'Figma File',
    icon: 'file',
  };
}

export function FigmaUrlInput({
  value = '',
  onChange,
  onSave,
  disabled = false,
  className,
  context = 'issue',
  showSaveButton = true,
  showLabel = true,
  size = 'default',
  saveButtonVariant = 'primary',
  saveButtonAccessory,
}: FigmaUrlInputProps) {
  const [url, setUrl] = useState(value);
  const [parsed, setParsed] = useState<ParsedFigmaUrl | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const feedbackId = useId();
  const hasVerdict = Boolean(url.trim() && parsed);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newUrl = e.target.value;
      setUrl(newUrl);
      setError(null);

      if (newUrl.trim()) {
        const result = parseFigmaUrl(newUrl);
        setParsed(result);
        onChange?.(newUrl, result.isValid ? result : null);
      } else {
        setParsed(null);
        onChange?.(newUrl, null);
      }
    },
    [onChange]
  );

  const handleSave = async () => {
    if (!parsed?.isValid) return;

    setIsSaving(true);
    setError(null);

    try {
      await onSave(parsed);
    } catch (err) {
      setError(toUserFacingMessage(err, 'Failed to save'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const pastedText = e.clipboardData.getData('text');
      if (pastedText.includes('figma.com')) {
        e.preventDefault();
        setUrl(pastedText);
        const result = parseFigmaUrl(pastedText);
        setParsed(result);
        onChange?.(pastedText, result.isValid ? result : null);
      }
    },
    [onChange]
  );

  return (
    <div className={clsx('space-y-3', className)}>
      <div>
        {showLabel && (
          <label htmlFor="figma-url" className="field-label">
            Figma URL
          </label>
        )}
        <input
          id="figma-url"
          type="url"
          value={url}
          onChange={handleChange}
          onPaste={handlePaste}
          placeholder={
            context === 'project'
              ? 'Paste Figma file URL...'
              : 'Paste Figma frame URL (or file URL to browse frames)...'
          }
          disabled={disabled}
          aria-invalid={Boolean(parsed && !parsed.isValid && url.trim())}
          aria-describedby={feedbackId}
          className={clsx(
            'input w-full',
            size === 'lg' && 'input-lg',
            parsed && !parsed.isValid && url.trim() && 'input-error'
          )}
        />
        {/* The verdict on the link. Always mounted, because screen readers
            announce a live region that already exists far more reliably than
            one inserted together with its message; it takes no space until
            there is a verdict (2026-10-25 audit, finding 1). */}
        <div
          id={feedbackId}
          aria-live="polite"
          className={clsx(
            hasVerdict && parsed && [
              'mt-3 text-sm p-3 rounded-lg border',
              parsed.isValid
                ? parsed.linkType === 'frame'
                  ? 'bg-lavender-50 text-lavender-700 border-lavender-200' // Frame links in lavender
                  : 'bg-primary-50 text-primary-700 border-primary-200' // File links in primary
                : 'bg-red-50 text-red-700 border-red-200',
            ]
          )}
        >
          {hasVerdict && parsed && (parsed.isValid ? (
            <div className="space-y-2">
              <div className="font-medium flex items-center gap-2">
                {parsed.linkType === 'frame' ? (
                  <HugeiconsIcon icon={Image01Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <HugeiconsIcon icon={Folder01Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" aria-hidden="true" />
                )}
                <span>
                  {parsed.linkType === 'frame'
                    ? `Linking to specific frame${parsed.fileName ? ` in ${parsed.fileName}` : ''}`
                    : `Linking to entire Figma file${parsed.fileName ? `: ${parsed.fileName}` : ''}`}
                </span>
              </div>
              {/* A frame link is taken as-is, and since 2026-09-10 the frame
                  is PERSISTED (projects.figma_node_id) so both pickers open on
                  it. Before that only the file key was stored, and this line
                  apologised for it; the old "Link to entire file instead"
                  nudge (removed 2026-09-09) steered users away from the link
                  every peer tool accepts (Storybook, Framer, Zeplin). Measured
                  2026-09-09: a trial user pasted a frame URL three times, took
                  the nudge, then scrolled a 101-frame picker for that frame. */}
              {context === 'project' && parsed.linkType === 'frame' && (
                <div className="mt-2 text-xs">
                  The picker opens on this frame, and the rest of the file stays one search&nbsp;away.
                </div>
              )}
              {context === 'issue' && parsed.linkType === 'file' && (
                <div className="mt-2 p-3 bg-primary-50 border border-primary-200 rounded text-primary-700 text-xs">
                  <div className="flex items-start gap-2">
                    <HugeiconsIcon icon={BulbIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 flex-shrink-0 text-primary-500" aria-hidden="true" />
                    <div className="flex-1">
                      <strong>Suggestion:</strong> This links to the entire file. Consider selecting a specific frame for precise feedback.
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>{parsed.error}</div>
          ))}
        </div>
      </div>

      {error && (
        <div role="alert" className="alert-error text-sm">{error}</div>
      )}

      {showSaveButton && (
      <div className={saveButtonAccessory ? 'flex items-center gap-3' : undefined}>
      <button
        // A <button> defaults to type="submit", so inside a host form (the
        // Project Settings modal's General tab) Link would also submit the form.
        type="button"
        onClick={handleSave}
        disabled={disabled || !parsed?.isValid || isSaving}
        className={saveButtonVariant === 'secondary' ? 'btn-secondary btn-sm' : 'btn-primary w-full'}
      >
        <ModalAction kind="link">
        {isSaving
          ? 'Linking...'
          : parsed?.isValid
          ? parsed.linkType === 'frame'
            // A project persists the frame (file key + node id) since
            // 2026-09-10, so the button names what it saves on both contexts.
            ? 'Link Figma frame'
            : context === 'issue'
            ? 'Link entire file'
            : 'Link Figma file'
          : context === 'project'
          ? 'Link Figma file'
          : 'Link Figma frame'}
      </ModalAction></button>
      {saveButtonAccessory}
      </div>
      )}
    </div>
  );
}
